import { MAX_ELEMIS_INSTALMENTS } from './evenMonthlyTerm';

export interface CollectionCountChoice {
    readonly min: number;
    readonly max: number;
    /** The derived count clamped into [min, max] — displayed, and sent, until the user chooses. */
    readonly preselect: number;
}

export interface CollectionCountInput {
    allowChoice: boolean;
    /** The derived plan term in whole months; `undefined` when no term could be derived. */
    termMonths: number | undefined;
    /** `collection_lead_months`, 0 when unset. */
    leadMonths: number;
    /** The count the schedule would send today (even-monthly derivation's `numInstalments`). */
    derivedCount: number;
    minInstalments: number | undefined;
    maxInstalments: number | undefined;
    /** The selection will send `has_deposit: true` (a deposit, or the default/equal-split one),
     *  so one instalment is spent on the deposit and the floor is 2, not 1. */
    depositConsumesInstalment?: boolean;
}

/**
 * The allowed Number-of-Collections range, or `null` when the control cannot be offered. `max` is
 * capped at `termMonths − lead` so a chosen count can never collect past the servicing regime.
 */
export const collectionCountChoice = (input: CollectionCountInput): CollectionCountChoice | null => {
    if (!input.allowChoice || input.termMonths === undefined) {
        return null;
    }
    const lead = input.leadMonths > 0 ? input.leadMonths : 0;
    const max = Math.min(
        input.termMonths - lead,
        input.maxInstalments ?? Number.POSITIVE_INFINITY,
        MAX_ELEMIS_INSTALMENTS,
    );
    const absoluteFloor = input.depositConsumesInstalment ? 2 : 1;
    const min = Math.max(absoluteFloor, input.minInstalments ?? absoluteFloor);
    if (!Number.isInteger(max) || max < min) {
        return null;
    }
    const preselect = Math.min(Math.max(input.derivedCount, min), max);
    return { min, max, preselect };
};

/**
 * The chosen value when it sits inside the offered range, else `undefined` — a stale persisted
 * choice (the term shrank on resume) is never sent.
 */
export const resolveChosenInstalments = (
    choice: CollectionCountChoice | null,
    chosen: number | undefined,
): number | undefined => {
    if (choice === null || chosen === undefined) {
        return undefined;
    }
    return chosen >= choice.min && chosen <= choice.max ? chosen : undefined;
};

/**
 * The count to transmit while the control is offered: the in-range chosen value, else the clamped
 * preselect the control displays, so an untouched control never sends an unclamped derived count.
 */
export const transmittedCollectionCount = (
    choice: CollectionCountChoice | null,
    chosen: number | undefined,
): number | undefined =>
    choice === null ? undefined : (resolveChosenInstalments(choice, chosen) ?? choice.preselect);
